import {
  ScreenCriterion,
  ScreenMetric,
  SmartBetaBasket,
  SmartBetaWeight,
  WeightingMethod,
  SmartBetaView,
  NthDay,
  NthOrdinal,
  SmartBetaSchedule,
  SmartBetaSettings,
  Weekday,
} from '../../services/smartBetaApi';

/** The editable form: both monthly modes kept so switching tabs does not lose input. */
export interface SmartBetaForm {
  frequency: 'WEEKLY' | 'MONTHLY';
  weeks: number | null;
  weekdays: Weekday[];
  months: number | null;
  monthlyMode: 'DAYS' | 'NTH';
  monthDays: number[];
  nth: NthOrdinal;
  nthDay: NthDay;
  cashWeight: number | null;
  /** weight null = auto: a share of what cash and the fixed weights leave, by `weightingMethod`. */
  whitelist: { symbol: string; weight: number | null }[];
  blacklist: string[];
  weightingMethod: WeightingMethod;
  /** Screens whose picks join as auto tickers. */
  criteria: ScreenCriterion[];
  screenLimit: number;
}

export const EMPTY_FORM: SmartBetaForm = {
  frequency: 'MONTHLY',
  weeks: 1,
  weekdays: ['WED'],
  months: 1,
  monthlyMode: 'DAYS',
  monthDays: [5],
  nth: 'FIRST',
  nthDay: 'WEEKDAY',
  cashWeight: 0,
  whitelist: [],
  blacklist: [],
  weightingMethod: 'EQUAL',
  criteria: [],
  screenLimit: 10,
};

export const MAX_TICKERS = 30;

export const WEIGHTING_OPTIONS: { value: WeightingMethod; label: string; available: boolean }[] = [
  { value: 'EQUAL', label: 'Equal', available: true },
  { value: 'MARKET_CAP', label: 'Market-capitalization', available: false },
  { value: 'INDUSTRY_MARKET_CAP', label: 'Industry Market-Capitalization', available: false },
];

export interface ScreenOption {
  metric: ScreenMetric;
  label: string;
  unit?: string;
  kind: 'pick' | 'range';
  description: string;
  /** A range whose figure can be below zero. */
  signed?: boolean;
}

/** Every screen, in the order they are offered: universe first, then price, volume, foreign flows. */
export const SCREEN_OPTIONS: ScreenOption[] = [
  { metric: 'EXCHANGE', label: 'Exchange', kind: 'pick', description: 'Only stocks listed on the chosen exchanges.' },
  { metric: 'INDEX', label: 'Index member', kind: 'pick', description: 'Only stocks in the index today, e.g. VN30.' },
  {
    metric: 'CHANGE_5D',
    label: 'Price change (5 sessions)',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'Close-to-close change over the last 5 sessions.',
  },
  {
    metric: 'CHANGE_20D',
    label: 'Price change (20 sessions)',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'Close-to-close change over the last 20 sessions, about one month.',
  },
  {
    metric: 'CHANGE_60D',
    label: 'Price change (60 sessions)',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'Close-to-close change over the last 60 sessions, about three months.',
  },
  {
    metric: 'PRICE_VS_MA50',
    label: 'Price vs 50-session average',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'How far the last close is above (+) or below (−) its 50-session moving average.',
  },
  {
    metric: 'FROM_HIGH_60D',
    label: 'Distance from 60-session high',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'How far the last close is below its highest close of the last 60 sessions. 0 means at the high.',
  },
  {
    metric: 'VOLATILITY_20D',
    label: 'Volatility (20 sessions)',
    unit: '% a year',
    kind: 'range',
    description:
      'Standard deviation of daily returns over the last 20 sessions, annualised. Higher means bigger swings.',
  },
  {
    metric: 'RSI_14',
    label: 'RSI (14 sessions)',
    unit: '0–100',
    kind: 'range',
    description: 'Relative strength index. Above 70 is often read as overbought, below 30 as oversold.',
  },
  {
    metric: 'AVG_VALUE_20D',
    label: 'Avg traded value (20 sessions)',
    unit: 'bn VND',
    kind: 'range',
    description: 'Average daily traded value over the last 20 sessions — how liquid the stock is.',
  },
  {
    metric: 'VOLUME_RATIO_5_20',
    label: 'Volume surge (5 vs 20 sessions)',
    unit: '×',
    kind: 'range',
    description:
      'Average volume of the last 5 sessions divided by that of the last 20. Above 1 means trading is picking up.',
  },
  {
    metric: 'FOREIGN_NET_VALUE_20D',
    label: 'Foreign net buying (20 sessions)',
    unit: 'bn VND',
    kind: 'range',
    signed: true,
    description: 'Foreign investors’ buy value minus sell value over the last 20 sessions. Negative means net selling.',
  },
  {
    metric: 'FOREIGN_NET_RATIO_20D',
    label: 'Foreign net buying share (20 sessions)',
    unit: '%',
    kind: 'range',
    signed: true,
    description: 'Foreign net bought volume as a share of all volume traded over the last 20 sessions.',
  },
];

export const WEEKDAY_LABEL: Record<Weekday, string> = { MON: 'M', TUE: 'T', WED: 'W', THU: 'T', FRI: 'F' };
export const WEEKDAY_NAME: Record<Weekday, string> = {
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
};
export const NTH_LABEL: Record<NthOrdinal, string> = {
  FIRST: 'First',
  SECOND: 'Second',
  THIRD: 'Third',
  FOURTH: 'Fourth',
  LAST: 'Last',
};
export const NTH_DAY_LABEL: Record<NthDay, string> = { DAY: 'Day', WEEKDAY: 'Weekday', ...WEEKDAY_NAME };

export function formFromSettings(s: SmartBetaSettings): SmartBetaForm {
  const f: SmartBetaForm = {
    ...EMPTY_FORM,
    frequency: s.schedule.frequency,
    cashWeight: s.cashWeight,
    whitelist: s.whitelist.map((w) => ({ ...w })),
    blacklist: [...s.blacklist],
    weightingMethod: s.weightingMethod ?? 'EQUAL',
    criteria: (s.criteria ?? []).map((c) => ({ ...c })),
    screenLimit: s.screenLimit || EMPTY_FORM.screenLimit,
  };
  const sch = s.schedule;
  if (sch.frequency === 'WEEKLY') return { ...f, weeks: sch.interval, weekdays: sch.weekdays };
  if (sch.mode === 'DAYS') return { ...f, months: sch.interval, monthlyMode: 'DAYS', monthDays: sch.monthDays };
  return { ...f, months: sch.interval, monthlyMode: 'NTH', nth: sch.nth, nthDay: sch.nthDay };
}

export function scheduleOf(f: SmartBetaForm): SmartBetaSchedule {
  if (f.frequency === 'WEEKLY') return { frequency: 'WEEKLY', interval: f.weeks ?? 0, weekdays: f.weekdays };
  if (f.monthlyMode === 'DAYS') {
    return { frequency: 'MONTHLY', interval: f.months ?? 0, mode: 'DAYS', monthDays: f.monthDays };
  }
  return { frequency: 'MONTHLY', interval: f.months ?? 0, mode: 'NTH', nth: f.nth, nthDay: f.nthDay };
}

export function settingsOf(f: SmartBetaForm): SmartBetaSettings {
  return {
    schedule: scheduleOf(f),
    cashWeight: f.cashWeight ?? 0,
    whitelist: f.whitelist.map((w) => ({ symbol: w.symbol, weight: w.weight ?? null })),
    blacklist: f.blacklist,
    criteria: f.criteria,
    ...(f.criteria.length ? { screenLimit: f.screenLimit } : {}),
    weightingMethod: f.weightingMethod,
  };
}

export function basketOf(f: SmartBetaForm): SmartBetaBasket {
  const { schedule, ...basket } = settingsOf(f);
  return basket;
}

/**
 * The weight each ticker trades to — Core's `targetWeights`: fixed as entered, auto ones sharing
 * what is left equally in hundredths, the leftover hundredths to the first auto ticker.
 */
export function targetWeights(basket: {
  cashWeight: number | null;
  whitelist: { symbol: string; weight: number | null }[];
  screened?: string[];
}): SmartBetaWeight[] {
  // Screened tickers are auto tickers after the white list's own.
  const own = new Set(basket.whitelist.map((w) => w.symbol));
  const b = {
    cashWeight: basket.cashWeight,
    whitelist: [
      ...basket.whitelist,
      ...(basket.screened ?? []).filter((s) => !own.has(s)).map((symbol) => ({ symbol, weight: null })),
    ],
  };
  const autos = b.whitelist.filter((w) => w.weight == null);
  const fixed = b.whitelist.reduce((s, w) => s + (w.weight ?? 0), b.cashWeight ?? 0);
  const left = Math.max(Math.round((100 - fixed) * 100), 0);
  const each = autos.length ? Math.floor(left / autos.length) : 0;
  const first = autos[0]?.symbol;
  return b.whitelist.map((w) => ({
    symbol: w.symbol,
    weight: w.weight ?? (w.symbol === first ? each + (left - each * autos.length) : each) / 100,
  }));
}

/** The next-portfolio editor: the saved next basket, else a copy of the current one to edit from. */
export function nextFormOf(view: SmartBetaView): SmartBetaForm {
  const base = formFromSettings(view.settings);
  const next = view.next;
  return next
    ? {
        ...base,
        cashWeight: next.cashWeight,
        whitelist: next.whitelist.map((w) => ({ ...w })),
        blacklist: [...next.blacklist],
        weightingMethod: next.weightingMethod ?? 'EQUAL',
        criteria: (next.criteria ?? []).map((c) => ({ ...c })),
        screenLimit: next.screenLimit || EMPTY_FORM.screenLimit,
      }
    : base;
}

/** Cash + every ticker, rounded to cents so 33.33 × 3 + 0.01 reads as 100. */
export function totalWeight(f: SmartBetaForm): number {
  const sum = f.whitelist.reduce((s, w) => s + (w.weight ?? 0), f.cashWeight ?? 0);
  return Math.round(sum * 100) / 100;
}

/** What is wrong with the schedule alone, or null. */
export function scheduleProblem(f: SmartBetaForm): string | null {
  if (f.frequency === 'WEEKLY') {
    if (!f.weeks || f.weeks < 1) return 'Enter how many weeks between rebalances';
    if (f.weekdays.length === 0) return 'Pick at least one weekday';
  } else {
    if (!f.months || f.months < 1) return 'Enter how many months between rebalances';
    if (f.monthlyMode === 'DAYS' && f.monthDays.length === 0) return 'Pick at least one day of the month';
  }
  return null;
}

/** The first thing stopping a save, worded for the user; null when the form can be saved. */
export function formProblem(f: SmartBetaForm): string | null {
  return scheduleProblem(f) ?? basketProblem(f);
}

/** What is wrong with the tickers and weights alone, or null. */
export function basketProblem(f: SmartBetaForm): string | null {
  if (f.whitelist.length === 0 && f.criteria.length === 0) return 'Add a ticker to the white list or a screen';
  if (f.whitelist.some((w) => !w.symbol)) return 'Every white list row needs a ticker';
  const screen = screenProblem(f);
  if (screen) return screen;
  if (f.whitelist.some((w) => w.weight != null && w.weight <= 0))
    return 'A fixed weight must be above 0% (leave it empty for auto)';
  const total = totalWeight(f);
  // A screen adds auto tickers too, though only the screen knows how many.
  const autos = f.whitelist.filter((w) => w.weight == null).length + (f.criteria.length ? 1 : 0);
  if (autos === 0 && total !== 100) return `Cash and tickers must add up to 100% (now ${total}%)`;
  if (autos > 0 && 100 - total < autos * 0.01) {
    return `Cash and fixed weights take ${total}% — nothing is left for the auto tickers`;
  }
  const clash = f.whitelist.find((w) => f.blacklist.includes(w.symbol));
  if (clash) return `${clash.symbol} is on both the white and the black list`;
  return null;
}

/** What is wrong with the screens alone, or null. */
export function screenProblem(f: SmartBetaForm): string | null {
  for (const c of f.criteria) {
    const label = SCREEN_OPTIONS.find((o) => o.metric === c.metric)?.label ?? c.metric;
    if (c.metric === 'EXCHANGE' || c.metric === 'INDEX') {
      if (!c.values?.length) return `${label}: pick at least one`;
    } else {
      if (c.min == null && c.max == null) return `${label}: enter a min or a max`;
      if (c.min != null && c.max != null && c.min > c.max) return `${label}: min is above max`;
    }
  }
  if (f.criteria.length) {
    if (!(f.screenLimit >= 1)) return 'Screens must keep at least 1 ticker';
    if (f.whitelist.length + f.screenLimit > MAX_TICKERS) {
      return `White list and screened tickers come to at most ${MAX_TICKERS} together`;
    }
  }
  return null;
}

/** One line for the header, e.g. "Every 2 months on the first weekday". */
export function describeSchedule(s: SmartBetaSchedule): string {
  const every = (n: number, unit: string) => (n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`);
  if (s.frequency === 'WEEKLY') {
    return `${every(s.interval, 'week')} on ${s.weekdays.map((d) => WEEKDAY_NAME[d].slice(0, 3)).join(', ')}`;
  }
  if (s.mode === 'DAYS') {
    const days = s.monthDays.map(ordinal).join(', ');
    return `${every(s.interval, 'month')} on the ${days}`;
  }
  return `${every(s.interval, 'month')} on the ${NTH_LABEL[s.nth].toLowerCase()} ${NTH_DAY_LABEL[s.nthDay].toLowerCase()}`;
}

function ordinal(n: number): string {
  const tail =
    n % 100 >= 11 && n % 100 <= 13 ? 'th' : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th');
  return `${n}${tail}`;
}

/** Same split as Core's: tickers only, by weight, whole VND, remainder to the heaviest. */
export function splitPreview(amount: number, whitelist: { symbol: string; weight: number }[]): Record<string, number> {
  const total = whitelist.reduce((s, w) => s + w.weight, 0);
  if (!(amount > 0) || total <= 0) return {};
  const whole = Math.floor(amount);
  const out: Record<string, number> = {};
  let given = 0;
  for (const w of whitelist) {
    out[w.symbol] = Math.floor((whole * w.weight) / total);
    given += out[w.symbol];
  }
  const heaviest = whitelist.reduce((a, b) => (b.weight > a.weight ? b : a));
  out[heaviest.symbol] += whole - given;
  return out;
}
